import { AnimatePresence, motion, type TargetAndTransition, type Transition } from 'framer-motion';
import type { QueueEntry } from '../hooks/useMediaQueue';
import { AMBIENT_SCENES } from '../media/ambientScenes';
import { useSettings, type MediaItem, type TransitionId } from '../store/settings';

interface Props {
  entry: QueueEntry | null;
  cycle: number;
  intervalSec: number;
  onEnded: () => void;
  onError: (id: string) => void;
  /** Loop videos when they are the only item, so the screen never freezes on the last frame. */
  loopVideo: boolean;
}

interface Variant {
  initial: TargetAndTransition;
  animate: TargetAndTransition;
  exit: TargetAndTransition;
  transition: Transition;
}

/** `cycle` (the slide counter) picks the Ken Burns pan direction, so it stays fixed for the
 *  life of a slide even when the stage re-renders. */
function variantFor(t: TransitionId, intervalSec: number, cycle: number): Variant {
  switch (t) {
    case 'slide':
      return {
        initial: { x: '100%', opacity: 1 },
        animate: { x: 0, opacity: 1 },
        exit: { x: '-100%', opacity: 1 },
        transition: { duration: 1.1, ease: [0.65, 0, 0.35, 1] },
      };
    case 'dissolve':
      return {
        initial: { opacity: 0, filter: 'blur(12px)' },
        animate: { opacity: 1, filter: 'blur(0px)' },
        exit: { opacity: 0, filter: 'blur(12px)' },
        transition: { duration: 2.4, ease: 'easeInOut' },
      };
    case 'kenburns': {
      // Slow zoom/pan for the whole time the slide is on screen.
      const dir = cycle % 2 === 0 ? 1 : -1;
      return {
        initial: { opacity: 0, scale: 1.02, x: 0 },
        animate: {
          opacity: 1,
          scale: 1.14,
          x: `${dir * 2}%`,
          transition: {
            opacity: { duration: 1.6, ease: 'easeInOut' },
            scale: { duration: intervalSec + 2, ease: 'linear' },
            x: { duration: intervalSec + 2, ease: 'linear' },
          },
        },
        exit: { opacity: 0, transition: { duration: 1.6, ease: 'easeInOut' } },
        transition: { duration: 1.6 },
      };
    }
    default:
      return {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 1.4, ease: 'easeInOut' },
      };
  }
}

export function MediaStage({ entry, cycle, intervalSec, onEnded, onError, loopVideo }: Props) {
  const transition = useSettings((s) => s.media.transition);
  const overlayColor = useSettings((s) => s.media.overlayColor);
  const overlayOpacity = useSettings((s) => s.media.overlayOpacity);
  const v = variantFor(transition, intervalSec, cycle);

  return (
    <div className="absolute inset-0 overflow-hidden bg-black" aria-hidden="true">
      <AnimatePresence initial={false}>
        {entry && (
          <motion.div
            key={cycle}
            className="absolute inset-0 will-change-transform"
            initial={v.initial}
            animate={v.animate}
            exit={v.exit}
            transition={v.transition}
          >
            <Slide entry={entry} onEnded={onEnded} onError={onError} loop={loopVideo} />
          </motion.div>
        )}
      </AnimatePresence>
      {/* Shading overlay keeps widgets readable over bright photos. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ backgroundColor: overlayColor, opacity: overlayOpacity }}
      />
    </div>
  );
}

interface SlideProps {
  entry: QueueEntry;
  onEnded: () => void;
  onError: (id: string) => void;
  loop: boolean;
}

function Slide({ entry, onEnded, onError, loop }: SlideProps) {
  if (entry.type === 'ambient') {
    const scene = AMBIENT_SCENES[entry.sceneIndex] ?? AMBIENT_SCENES[0]!;
    return <div className="absolute inset-0" style={{ background: scene.layers.join(', ') }} />;
  }
  return <MediaSlide item={entry.item} onEnded={onEnded} onError={onError} loop={loop} />;
}

function MediaSlide({ item, onEnded, onError, loop }: Omit<SlideProps, 'entry'> & { item: MediaItem }) {
  const fill = useSettings((s) => s.media.fit === 'cover');
  if (item.kind === 'video') {
    return (
      <video
        className={`absolute inset-0 h-full w-full ${fill ? 'object-cover' : 'object-contain'}`}
        src={item.url}
        autoPlay
        muted
        loop={loop}
        playsInline
        preload="auto"
        onEnded={onEnded}
        onError={() => onError(item.id)}
      />
    );
  }
  return (
    <>
      {/* Fit mode: blurred fill behind portrait / odd-ratio photos instead of black bars. */}
      {!fill && (
        <img src={item.url} alt="" className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl brightness-75" />
      )}
      <img
        src={item.url}
        alt={item.title ?? ''}
        className={`absolute inset-0 h-full w-full ${fill ? 'object-cover' : 'object-contain'}`}
        decoding="async"
        onError={() => onError(item.id)}
      />
    </>
  );
}
